import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../../../../ui/InventorySlideOver';
import {
  getAdminRestaurantsList,
  fetchQuantityRecheckTargets,
  quantityCheckAICheckerEmail,
  type AdminRestaurant,
  type QuantityRecheckTarget,
} from '../../../../../../services/admin/adminService';
import {
  mainCategoryDisplayMap,
  foodCategoryDisplayMap,
  drinksCategoryDisplayMap,
  nonFoodCategoryDisplayMap,
} from '../../../../../orderhistory/categoryUtils';
import { fmtLocalDate } from '../ranges';
import type { TFn } from '../../types';

const MONTH_PRESETS = [1, 2, 3] as const;
const MAIN_CATEGORIES = ['FOOD', 'DRINKS', 'NON_FOOD'] as const;
const CATEGORY_GROUPS: Record<string, Record<string, string>> = {
  FOOD: foodCategoryDisplayMap,
  DRINKS: drinksCategoryDisplayMap,
  NON_FOOD: nonFoodCategoryDisplayMap,
};

const monthsAgo = (months: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return fmtLocalDate(d);
};

type RunState = { done: number; total: number; flags: number; failures: number; finished: boolean };

/**
 * Scoped quantity recheck: one restaurant, a date range, optionally some suppliers and categories.
 * Loops the existing per-email quantity check (sequential — Gemini is the bottleneck) with the scope,
 * so fresh flags land on each email's audit row and show up in the Issues tab, where Ask customer,
 * Apply override and the code-monkey fix prompt already work. Flags on lines outside the scope are kept.
 */
export function QuantityRecheckPanel({ open, onClose, onDone, t }: {
  open: boolean; onClose: () => void; onDone: () => void; t: TFn;
}) {
  const [restaurants, setRestaurants] = useState<AdminRestaurant[]>([]);
  const [search, setSearch] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [startDate, setStartDate] = useState(() => monthsAgo(1));
  const [endDate, setEndDate] = useState(() => fmtLocalDate(new Date()));
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [mainCategory, setMainCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [restaurantPlatforms, setRestaurantPlatforms] = useState<string[]>([]);
  const [targets, setTargets] = useState<QuantityRecheckTarget[] | null>(null);
  const [withoutAuditRow, setWithoutAuditRow] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState<RunState | null>(null);
  const stopRef = useRef(false);
  const running = !!run && !run.finished;

  useEffect(() => {
    if (!open || restaurants.length > 0) return;
    getAdminRestaurantsList().then(setRestaurants).catch((e) => setError(e.message || 'Failed'));
  }, [open, restaurants.length]);

  // The work list is a cheap read, so it follows the filters live and the button shows what a run costs.
  useEffect(() => {
    if (!open || !restaurantId || !startDate || !endDate) { setTargets(null); return; }
    let cancelled = false;
    setTargets(null);
    setError(null);
    fetchQuantityRecheckTargets(restaurantId, startDate, endDate, platforms)
      .then((r) => {
        if (cancelled) return;
        setTargets(r.targets);
        setWithoutAuditRow(r.withoutAuditRow);
        setRestaurantPlatforms(r.restaurantPlatforms);
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed'); });
    return () => { cancelled = true; };
  }, [open, restaurantId, startDate, endDate, platforms]);

  const filteredRestaurants = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? restaurants.filter((r) => r.name.toLowerCase().includes(q) || r.ownerEmail?.toLowerCase().includes(q)) : restaurants;
  }, [restaurants, search]);

  const chooseRestaurant = (id: string) => {
    setRestaurantId(id);
    setPlatforms([]);
    setRestaurantPlatforms([]);
    setRun(null);
  };

  const toggle = (list: string[], value: string) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const start = async () => {
    if (!targets || targets.length === 0 || running) return;
    if (!window.confirm(t('adminTesting.review.qtyRecheckConfirm', { count: targets.length }))) return;
    stopRef.current = false;
    const scope = {
      restaurantId,
      platforms,
      mainCategories: mainCategory ? [mainCategory] : [],
      categories,
    };
    const state: RunState = { done: 0, total: targets.length, flags: 0, failures: 0, finished: false };
    setRun({ ...state });
    for (const target of targets) {
      if (stopRef.current) break;
      try {
        const r = await quantityCheckAICheckerEmail(target.account, target.gmailMessageId, target.rfc822MessageId, scope);
        if (!r.updated || r.flagCount === null) state.failures++;
        else state.flags += r.flagCount;
      } catch (err) {
        state.failures++;
        console.error('Failed to recheck quantities for email', target.gmailMessageId, err);
      }
      state.done++;
      setRun({ ...state });
    }
    setRun({ ...state, finished: true });
    onDone();
  };

  const chipCls = (active: boolean) => cn(
    'px-2.5 py-1 rounded-full border text-xs transition-colors',
    active
      ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
      : cn('border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700', primaryText),
  );
  const inputCls = cn('w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize);
  const labelCls = cn('block text-xs mb-1', semiBoldText, primaryText);
  const presetActive = (months: number) => startDate === monthsAgo(months) && endDate === fmtLocalDate(new Date());
  const categoryGroup = mainCategory ? CATEGORY_GROUPS[mainCategory] : null;

  return (
    <InventorySlideOver
      isOpen={open}
      onClose={running ? () => undefined : onClose}
      title={t('adminTesting.review.qtyRecheckTitle')}
      description={t('adminTesting.review.qtyRecheckDesc')}
      closeLabel={t('adminTesting.review.qtyRecheckClose')}
      footer={
        <div className="flex gap-3">
          {running ? (
            <button onClick={() => { stopRef.current = true; }} className={slideOverCancelClass}>{t('adminTesting.review.qtyRecheckStop')}</button>
          ) : (
            <button onClick={onClose} className={slideOverCancelClass}>{t('adminTesting.review.qtyRecheckClose')}</button>
          )}
          <button onClick={start} disabled={running || !targets || targets.length === 0} className={slideOverPrimaryClass}>
            {t('adminTesting.review.qtyRecheckStart', { count: targets?.length ?? 0 })}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <label className={labelCls}>{t('adminTesting.review.qtyRecheckRestaurant')}</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('adminTesting.review.qtyRecheckSearch')} className={cn(inputCls, 'mb-2')} disabled={running} />
          <select value={restaurantId} onChange={(e) => chooseRestaurant(e.target.value)} className={inputCls} disabled={running}>
            <option value="">{t('adminTesting.review.qtyRecheckChoose')}</option>
            {filteredRestaurants.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.ownerEmail}</option>)}
          </select>
        </div>

        <div>
          <label className={labelCls}>{t('adminTesting.review.qtyRecheckPeriod')}</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {MONTH_PRESETS.map((m) => (
              <button key={m} disabled={running} onClick={() => { setStartDate(monthsAgo(m)); setEndDate(fmtLocalDate(new Date())); }} className={chipCls(presetActive(m))}>
                {t('adminTesting.review.qtyRecheckMonths', { count: m })}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} disabled={running} />
            <span className={cn(secondaryText, smallTextSize)}>→</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} disabled={running} />
          </div>
        </div>

        {restaurantPlatforms.length > 0 && (
          <div>
            <label className={labelCls}>{t('adminTesting.review.qtyRecheckSuppliers')}</label>
            <div className="flex flex-wrap gap-2">
              {restaurantPlatforms.map((p) => (
                <button key={p} disabled={running} onClick={() => setPlatforms((l) => toggle(l, p))} className={chipCls(platforms.includes(p))}>{p}</button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className={labelCls}>{t('adminTesting.review.qtyRecheckMainCategory')}</label>
          <div className="flex flex-wrap gap-2">
            <button disabled={running} onClick={() => { setMainCategory(''); setCategories([]); }} className={chipCls(!mainCategory)}>{t('adminTesting.review.qtyRecheckAll')}</button>
            {MAIN_CATEGORIES.map((m) => (
              <button key={m} disabled={running} onClick={() => { setMainCategory(m); setCategories([]); }} className={chipCls(mainCategory === m)}>{mainCategoryDisplayMap[m]}</button>
            ))}
          </div>
          {categoryGroup && (
            <>
              <label className={cn(labelCls, 'mt-3')}>{t('adminTesting.review.qtyRecheckCategories')}</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(categoryGroup).map(([value, label]) => (
                  <button key={value} disabled={running} onClick={() => setCategories((l) => toggle(l, value))} className={chipCls(categories.includes(value))}>{label}</button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className={cn('text-xs space-y-1', secondaryText)}>
          {restaurantId && !targets && !error && <div>{t('adminTesting.review.qtyRecheckLoading')}</div>}
          {targets && <div>{t('adminTesting.review.qtyRecheckFound', { count: targets.length })}</div>}
          {targets && withoutAuditRow > 0 && <div className="text-amber-700 dark:text-amber-300">{t('adminTesting.review.qtyRecheckWithoutAudit', { count: withoutAuditRow })}</div>}
          {run && (
            <div className={cn(primaryText, semiBoldText)}>
              {run.finished
                ? t('adminTesting.review.qtyRecheckDone', { done: run.done, total: run.total, flags: run.flags, failures: run.failures })
                : t('adminTesting.review.qtyRecheckProgress', { done: run.done, total: run.total, flags: run.flags })}
            </div>
          )}
          {error && <div className="text-red-600 dark:text-red-400">{error}</div>}
        </div>
      </div>
    </InventorySlideOver>
  );
}
